import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable no-param-reassign */


import { theme } from "../../theme";
import { easeCurves } from "../../motion/eases";
import { transitionTypes, DURATION } from "./transition-types";
/**
 * Hide a panel
 */

export function hidePanel(el) {
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'toggle';

  var _ref = arguments.length > 2 ? arguments[2] : undefined,
      before = _ref.before,
      opacity = _ref.opacity,
      gridArea = _ref.gridArea,
      activeTransition = _ref.activeTransition;

  var animationComplete = arguments.length > 3 ? arguments[3] : undefined;

  if (!el.animate || type === 'disabled') {
    animationComplete();
    return;
  }

  var _transitionTypes$type = transitionTypes[type].hide(),
      _transitionTypes$type2 = _slicedToArray(_transitionTypes$type.keyframes, 2),
      startKeyframe = _transitionTypes$type2[0],
      endKeyframe = _transitionTypes$type2[1],
      options = _transitionTypes$type.options; // Handle cases where we are interrupting an existing animation


  if (activeTransition.current) {
    startKeyframe.opacity = Number(opacity);
  }

  applyStyles(el, _objectSpread(_objectSpread({}, getStylesForToggling(before)), startKeyframe)); // Start our transition

  activeTransition.current = el.animate([startKeyframe, endKeyframe], _objectSpread({
    duration: DURATION,
    easing: easeCurves.primary
  }, options));

  activeTransition.current.onfinish = function () {
    // Upon finishing, reset our styles
    // This technique is explained in MDN and is an alternative
    // to using web-animations fill mode.
    // https://developer.mozilla.org/en-US/docs/Web/API/EffectTiming/fill#Alternatives_to_fill_modes
    // If "flashes" prove to be a problem, it might be required
    // to set the end keyframes as the inital style prior to running
    // the animation, which is more traditional for FLIP.
    activeTransition.current = false;
    resetStyles(el, gridArea);
    animationComplete();
  };
}
/**
 * Show a panel
 */

export function showPanel(el) {
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'toggle';

  var _ref2 = arguments.length > 2 ? arguments[2] : undefined,
      after = _ref2.after,
      gridArea = _ref2.gridArea,
      opacity = _ref2.opacity,
      activeTransition = _ref2.activeTransition;

  var animationComplete = arguments.length > 3 ? arguments[3] : undefined;

  if (!el.animate || type === 'disabled') {
    animationComplete();
    return;
  }

  var _transitionTypes$type3 = transitionTypes[type].show(),
      _transitionTypes$type4 = _slicedToArray(_transitionTypes$type3.keyframes, 2),
      startKeyframe = _transitionTypes$type4[0],
      endKeyframe = _transitionTypes$type4[1],
      options = _transitionTypes$type3.options;

  if (activeTransition.current) {
    startKeyframe.opacity = Number(opacity);
  }

  applyStyles(el, _objectSpread(_objectSpread({}, getStylesForToggling(after)), startKeyframe));
  activeTransition.current = el.animate([startKeyframe, endKeyframe], _objectSpread({
    duration: DURATION,
    easing: easeCurves.primary
  }, options));

  activeTransition.current.onfinish = function () {
    activeTransition.current = false;
    resetStyles(el, gridArea);
    animationComplete();
  };
}
/**
 * Resize or reposition a panel
 */

export function resizePanel(el, type, _ref3, transitionEnd) {
  var before = _ref3.before,
      after = _ref3.after,
      deltaX = _ref3.deltaX,
      deltaY = _ref3.deltaY,
      w = _ref3.w,
      h = _ref3.h,
      gridArea = _ref3.gridArea,
      activeTransition = _ref3.activeTransition;

  if (!el.animate || type === 'disabled') {
    transitionEnd();
    return;
  }

  var startKeyframe = {
    width: activeTransition.current ? w : "".concat(before.width, "px"),
    height: activeTransition.current ? h : "".concat(before.height, "px"),
    transform: "translateX(".concat(deltaX, "px) translateY(").concat(deltaY, "px)")
  };
  var endKeyframe = {
    width: "".concat(after.width, "px"),
    height: "".concat(after.height, "px"),
    transform: "translateX(0) translateY(0)"
  };
  applyStyles(el, _objectSpread(_objectSpread(_objectSpread({}, getStylesForToggling(after)), startKeyframe), {}, {
    zIndex: theme.zIndices.panel
  }));
  activeTransition.current = el.animate([startKeyframe, endKeyframe], {
    duration: DURATION - 50,
    easing: 'cubic-bezier(0.5, 0, 0, 1)' // custom primary

  });

  activeTransition.current.onfinish = function () {
    activeTransition.current = null;
    resetStyles(el, gridArea);
    transitionEnd();
  };
}
export function flipResize(el, type, _ref4, transitionEnd) {
  var after = _ref4.after,
      gridArea = _ref4.gridArea,
      activeTransition = _ref4.activeTransition,
      keyframes = _ref4.keyframes;

  if (!el.animate || type === 'disabled') {
    transitionEnd();
  }

  applyStyles(el, _objectSpread(_objectSpread({}, getStylesForToggling(after)), {}, {
    transformOrigin: 'top left',
    zIndex: theme.zIndices.panel
  }));
  activeTransition.current = el.animate(keyframes, {
    duration: DURATION,
    easing: easeCurves.primary
  });

  activeTransition.current.onfinish = function () {
    activeTransition.current = null;
    resetStyles(el, gridArea);
    transitionEnd();
  };
}
/**
 * Remove any styles defined in an object
 */

function resetStyles(el, gridArea) {
  el.style.cssText = "grid-area: ".concat(gridArea);
}
/**
 * Shared styling for toggled panels
 */


function getStylesForToggling(target) {
  return {
    position: 'fixed',
    width: "".concat(target.width, "px"),
    height: "".concat(target.height, "px"),
    top: "".concat(target.top, "px"),
    left: "".concat(target.left, "px"),
    gridArea: null,
    zIndex: theme.zIndices.panel - 1,
    transformOrigin: 'center'
  };
}
/**
 * Apply an object of styles to an element
 */


function applyStyles(el, styles) {
  _extends(el.style, styles);
}